import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Languages, Menu, X } from "lucide-react";
import { useBooking } from "../BookingContext";

export const Navigation = ({ t, lang, toggleLang }) => {
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "method", label: t.nav.method },
    { id: "consulting", label: t.nav.consulting },
    { id: "about", label: t.nav.about },
  ];

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/80 border-b border-black/10" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img src="/mylingomap-logo.png" alt="My Lingo Map" className="h-14 w-auto object-contain md:h-16" />
        </a>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-semibold text-black/70 transition-colors hover:text-[#0066FF]"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 rounded-full border border-black/15 px-3 py-1.5 text-xs font-bold text-black/70 transition-colors hover:border-[#0066FF] hover:text-[#0066FF]"
          >
            <Languages className="h-3.5 w-3.5" />
            {lang === "ja" ? "EN" : "日本語"}
          </button>
          <button
            onClick={() => openBooking("adults")}
            data-testid="nav-book-button"
            className="rounded-full bg-[#FF6B00] px-5 py-2.5 text-sm font-bold text-white shadow-[0_6px_0_0_#c85400] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_0_0_#c85400]"
          >
            {t.nav.book}
          </button>
        </div>

        <button
          className="md:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-black/10 bg-white/95 px-6 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-base font-semibold text-black/80"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={toggleLang}
              className="self-start rounded-full border border-black/15 px-3 py-1.5 text-sm font-bold"
              data-testid="mobile-lang-toggle"
            >
              {lang === "ja" ? "English" : "日本語"}
            </button>
            <button
              onClick={() => {
                setOpen(false);
                openBooking("adults");
              }}
              className="rounded-full bg-[#FF6B00] px-5 py-3 text-center text-base font-bold text-white"
              data-testid="mobile-book-button"
            >
              {t.nav.book}
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
};
